
import { useState, useCallback } from 'react';
import { Coordinates } from '../types';
import { getDistance } from '../services/geometryUtils';
import { calculatePolygonArea, formatDistance, formatArea } from '../services/myContentService';

export type DrawingState = 'idle' | 'selecting_tool' | 'drawing' | 'saving' | 'editing';
export type DrawingToolType = 'line' | 'area';

export interface DrawingModeState {
  state: DrawingState;
  toolType: DrawingToolType | null;
  points: [number, number][];
  editingContentId: string | null;
  // Computed
  totalDistance: number;
  totalArea: number;
  formattedDistance: string;
  formattedArea: string;
}

export function useDrawingMode() {
  const [state, setState] = useState<DrawingState>('idle');
  const [toolType, setToolType] = useState<DrawingToolType | null>(null);
  const [points, setPoints] = useState<[number, number][]>([]);
  const [editingContentId, setEditingContentId] = useState<string | null>(null);

  const totalDistance = points.length >= 2
    ? points.reduce((acc, p, i) => {
        if (i === 0) return 0;
        return acc + getDistance(points[i - 1][0], points[i - 1][1], p[0], p[1]);
      }, 0)
    : 0;

  const totalArea = toolType === 'area' && points.length >= 3
    ? calculatePolygonArea(points)
    : 0;

  const openToolSelector = useCallback(() => {
    setState('selecting_tool');
  }, []);

  const startDrawing = useCallback((tool: DrawingToolType) => {
    setToolType(tool);
    setPoints([]);
    setState('drawing');
  }, []);

  const addPoint = useCallback((lat: number, lon: number) => {
    setPoints(prev => [...prev, [lat, lon]]);
  }, []);

  const undoLastPoint = useCallback(() => {
    setPoints(prev => prev.slice(0, -1));
  }, []);

  const finishDrawing = useCallback(() => {
    setState('saving');
  }, []);

  const cancelDrawing = useCallback(() => {
    setState('idle');
    setToolType(null);
    setPoints([]);
    setEditingContentId(null);
  }, []);

  const resetAfterSave = useCallback(() => {
    setState('idle');
    setToolType(null);
    setPoints([]);
  }, []);

  const startEditing = useCallback((contentId: string, existingPoints: [number, number][], type: DrawingToolType) => {
    setEditingContentId(contentId);
    setToolType(type);
    setPoints(existingPoints);
    setState('editing');
  }, []);

  const updateEditPoint = useCallback((index: number, lat: number, lon: number) => {
    setPoints(prev => {
      const next = [...prev];
      next[index] = [lat, lon];
      return next;
    });
  }, []);

  const insertEditPoint = useCallback((afterIndex: number, lat: number, lon: number) => {
    setPoints(prev => {
      const next = [...prev];
      next.splice(afterIndex + 1, 0, [lat, lon]);
      return next;
    });
  }, []);

  const removeEditPoint = useCallback((index: number) => {
    setPoints(prev => prev.filter((_, i) => i !== index));
  }, []);

  const canFinish = toolType === 'line' ? points.length >= 2 : points.length >= 3;

  return {
    state,
    toolType,
    points,
    editingContentId,
    totalDistance,
    totalArea,
    formattedDistance: formatDistance(totalDistance),
    formattedArea: formatArea(totalArea),
    canFinish,
    // Actions
    openToolSelector,
    startDrawing,
    addPoint,
    undoLastPoint,
    finishDrawing,
    cancelDrawing,
    resetAfterSave,
    startEditing,
    updateEditPoint,
    insertEditPoint,
    removeEditPoint,
  };
}
